// ==========================================================
// Psychrometrics — VPD, Dew Point, Humidity Deficit
// ==========================================================
// Pure functions. T in °C, RH in % (0-100).

// Tetens — saturation vapor pressure (kPa)
export function saturationVaporPressure(tempC: number): number {
  return 0.6108 * Math.exp((17.27 * tempC) / (tempC + 237.3));
}

// Actual vapor pressure (kPa)
export function actualVaporPressure(tempC: number, rhPercent: number): number {
  return saturationVaporPressure(tempC) * (rhPercent / 100);
}

// VPD (kPa) — how "dry" the air feels to the substrate
export function calculateVPD(tempC: number, rhPercent: number): number {
  const svp = saturationVaporPressure(tempC);
  return +(svp * (1 - rhPercent / 100)).toFixed(3);
}

// Magnus — dew point (°C)
export function calculateDewPoint(tempC: number, rhPercent: number): number {
  const a = 17.27;
  const b = 237.3;
  const rh = Math.max(0.01, Math.min(100, rhPercent));
  const alpha = Math.log(rh / 100) + (a * tempC) / (b + tempC);
  return +((b * alpha) / (a - alpha)).toFixed(1);
}

// Absolute humidity (g/m³) — ideal gas approximation
export function calculateAbsoluteHumidity(tempC: number, rhPercent: number): number {
  const svpPa = saturationVaporPressure(tempC) * 1000;
  const actualPa = svpPa * (rhPercent / 100);
  return +((actualPa * 2.1674) / (273.15 + tempC)).toFixed(2);
}

// Humidity deficit (g/m³) — saturation minus actual
export function calculateHumidityDeficit(tempC: number, rhPercent: number): number {
  const ahSat = calculateAbsoluteHumidity(tempC, 100);
  const ahNow = calculateAbsoluteHumidity(tempC, rhPercent);
  return +(ahSat - ahNow).toFixed(2);
}

export interface DerivedMetrics {
  vpd: number;              // kPa
  dewPoint: number;         // °C
  humidityDeficit: number;  // g/m³
  absoluteHumidity: number; // g/m³
}

export function deriveClimateMetrics(tempC: number, rhPercent: number): DerivedMetrics {
  return {
    vpd: calculateVPD(tempC, rhPercent),
    dewPoint: calculateDewPoint(tempC, rhPercent),
    humidityDeficit: calculateHumidityDeficit(tempC, rhPercent),
    absoluteHumidity: calculateAbsoluteHumidity(tempC, rhPercent),
  };
}
